// Karte „Beteiligte“ (Bereich „Bearbeitung“): je Rolle am Schaden ein Label,
// darunter die Personen als Pills mit Anruf- und Mail-Knopf. Quellen: der
// Schaden selbst (Versicherungsnehmer, Kontakte beim Versicherer), die Meldung
// (`beteiligteAusMeldung`) und frei hinzugefügte Beteiligte (`meldung.mappe`).
import * as React from "react";
import { useNavigate } from "react-router";
import { apiUrl } from "../../../utils/api";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import { useTelephonyOptional } from "../../../context/TelephonyContext";
import { useWindowManagerOptional } from "../../windowManager/WindowManagerContext";
import { openComposeWindow } from "../../mail/ComposeMailWindow";
import { KontaktmoeglichkeitCell } from "../../redesign/KontaktmoeglichkeitPopup";
import type { ContactDetailsFormValues } from "../../../types/Contact";
import "../../vorgang/v3/vorgangV3.css";
import { hinweisen } from "../../redesign/bestaetigen";
import { KontaktSuche, type Verweis } from "../meldung/KontaktSuche";
import type { MappeDamage } from "../types";
import { beteiligteAusMeldung } from "./beteiligteAusMeldung";
import { ROLLEN, type FreierBeteiligter, type MappeZusatz } from "./mappeDaten";

export interface BeteiligteKarteProps {
  damage: MappeDamage;
  answers: BigAnswers;
  mappe: MappeZusatz;
  onMappe: (patch: Partial<MappeZusatz>) => void;
}

interface Eintrag {
  key: string;
  rolle: string;
  name: string;
  contactId: number | null;
  tel: string;
  email: string;
  /** „Firma“ oder „Person“ — steuert das Kommunikations-Popup; unbekannt = Person. */
  typ?: string;
  /** Index in `mappe.beteiligte` — nur frei hinzugefügte lassen sich entfernen. */
  frei: number | null;
}

const IcoTel = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
  </svg>
);
const IcoMail = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

type KontaktZeile = { id: number; displayName?: string; lastname?: string | null; firstName?: string | null };

/**
 * Anzeigename wie überall in KMVS: `displayName` des Servers (Personen
 * „Nachname, Vorname“, Firmen der Firmenname), ersatzweise aus den Namensfeldern.
 * Leer = unbekannt (dann wird nachgeladen) — nie die Id.
 */
function nameAus(row: KontaktZeile | null | undefined): string {
  if (!row) return "";
  const anzeige = typeof row.displayName === "string" ? row.displayName.trim() : "";
  if (anzeige) return anzeige;
  return [row.lastname, row.firstName].map((x) => (x ?? "").trim()).filter(Boolean).join(", ");
}

/** Lädt die Namen der Kontakte nach, die die Mappe ohne Namen liefert. */
function useNachgeladeneNamen(ids: number[]): Record<number, string> {
  const [namen, setNamen] = React.useState<Record<number, string>>({});
  const schluessel = ids.join(",");
  React.useEffect(() => {
    let abgebrochen = false;
    for (const id of schluessel ? schluessel.split(",").map(Number) : []) {
      fetch(apiUrl(`contact/${id}`))
        .then((res) => (res.ok ? res.json() : null))
        .then((row) => {
          const name = nameAus(row);
          if (!abgebrochen && name) setNamen((alt) => (alt[id] === name ? alt : { ...alt, [id]: name }));
        })
        .catch(() => undefined);
    }
    return () => {
      abgebrochen = true;
    };
  }, [schluessel]);
  return namen;
}

function eintraege(
  damage: MappeDamage,
  answers: BigAnswers,
  mappe: MappeZusatz,
  nachgeladen: Record<number, string>,
): Eintrag[] {
  const liste: Eintrag[] = [];
  const kontakt = (rolle: string, c: { id: number; displayName?: string } | null | undefined) => {
    if (!c) return;
    const typ = (c as { type?: { type?: string } | null }).type?.type;
    liste.push({
      key: `k-${rolle}-${c.id}`,
      rolle,
      // Bis der Name geladen ist, ein neutraler Platzhalter — nie die Id.
      name: nameAus(c) || nachgeladen[c.id] || "…",
      contactId: c.id,
      tel: "",
      email: "",
      typ: typeof typ === "string" ? typ : undefined,
      frei: null,
    });
  };
  kontakt("Versicherungsnehmer", damage.contact);
  beteiligteAusMeldung(answers as unknown as Record<string, unknown>).forEach((b, i) =>
    liste.push({
      key: `m-${i}`,
      rolle: b.rolle,
      name: [b.vorname, b.name].filter(Boolean).join(" "),
      contactId: null,
      tel: b.tel,
      email: b.email,
      frei: null,
    }),
  );
  mappe.beteiligte.forEach((b, i) =>
    liste.push({
      key: `f-${i}`,
      rolle: b.rolle || "Beteiligter",
      name: (b.contactId != null && nachgeladen[b.contactId]) || b.name,
      contactId: b.contactId ?? null,
      tel: b.tel ?? "",
      email: b.email ?? "",
      frei: i,
    }),
  );
  kontakt("Sachbearbeiter VR", damage.sachbearbeiterVrContact);
  kontakt("Regulierer", damage.reguliererContact);
  kontakt("Sachverständiger", damage.sachverstaendigerContact);
  return liste;
}

/** Nach Rolle gruppiert, in der Reihenfolge des ersten Auftretens. */
function gruppen(liste: Eintrag[]): Array<{ rolle: string; personen: Eintrag[] }> {
  const aus: Array<{ rolle: string; personen: Eintrag[] }> = [];
  for (const e of liste) {
    const g = aus.find((x) => x.rolle === e.rolle);
    if (g) g.personen.push(e);
    else aus.push({ rolle: e.rolle, personen: [e] });
  }
  return aus;
}

const NEU_LEER = { rolle: ROLLEN[0] as string, kontakt: null as Verweis | null, name: "", tel: "", email: "" };

export function BeteiligteKarte({ damage, answers, mappe, onMappe }: BeteiligteKarteProps) {
  const navigate = useNavigate();
  const telefonie = useTelephonyOptional();
  const fenster = useWindowManagerOptional();
  const [offen, setOffen] = React.useState(false);
  const [neu, setNeu] = React.useState(NEU_LEER);

  // Kontakte, deren Namen die Mappe nicht mitliefert (oder die frei mit einem
  // Platzhalter gespeichert wurden) — einmal über den Kontakt-Endpunkt holen.
  const ohneName = React.useMemo(() => {
    const ids = new Set<number>();
    for (const c of [damage.contact, damage.sachbearbeiterVrContact, damage.reguliererContact, damage.sachverstaendigerContact]) {
      if (c && !nameAus(c)) ids.add(c.id);
    }
    for (const b of mappe.beteiligte) {
      if (b.contactId != null && (!b.name.trim() || /^Kontakt\s*#?\d+$/.test(b.name.trim()))) ids.add(b.contactId);
    }
    return [...ids].sort((a, b) => a - b);
  }, [damage, mappe.beteiligte]);
  const nachgeladen = useNachgeladeneNamen(ohneName);

  const liste = React.useMemo(
    () => eintraege(damage, answers, mappe, nachgeladen),
    [damage, answers, mappe, nachgeladen],
  );

  const anrufen = (nummer: string, name?: string) => {
    if (telefonie) telefonie.callNumber(nummer, name);
    else window.location.href = `tel:${nummer}`;
  };
  const mailen = (adresse: string, contactId?: number | null) => {
    if (fenster) {
      openComposeWindow(fenster.openWindow, {
        to: [adresse],
        subject: damage.damageNbr ? `Schaden ${damage.damageNbr}` : "",
        ...(damage.vorgangId ? { vorgangId: damage.vorgangId } : {}),
        ...(contactId ? { contactId } : {}),
      });
    } else {
      window.location.href = `mailto:${adresse}`;
    }
  };
  const fehlt = (was: string, name: string) =>
    void hinweisen({ titel: `Keine ${was} hinterlegt`, text: `Für ${name} ist keine ${was} hinterlegt.` });

  const schliessen = () => {
    setOffen(false);
    setNeu(NEU_LEER);
  };
  const name = neu.kontakt?.label || neu.name.trim();
  const hinzufuegen = () => {
    if (!name) return;
    const eintrag: FreierBeteiligter = {
      rolle: neu.rolle.trim() || "Beteiligter",
      name,
      contactId: neu.kontakt?.id ?? null,
      tel: neu.tel.trim(),
      email: neu.email.trim(),
    };
    onMappe({ beteiligte: [...mappe.beteiligte, eintrag] });
    schliessen();
  };

  return (
    <section className="smb-karte" aria-label="Beteiligte">
      <div className="smb-kt">
        <h2>Beteiligte</h2>
        <span className="smb-zahl">{liste.length}</span>
        <div className="smb-kt-re">
          <button type="button" className="smb-btn ist-pri" onClick={() => setOffen(true)}>
            <span aria-hidden="true">+</span> Beteiligten hinzufügen
          </button>
        </div>
      </div>

      {liste.length === 0 ? (
        <p className="smb-leer">Noch keine Beteiligten.</p>
      ) : (
        <div className="smb-bet">
          {gruppen(liste).map((g) => (
            <div key={g.rolle} className="smb-bt">
              <span className="smb-lbl">{g.rolle}</span>
              {g.personen.map((p) => (
                // Dieselbe Zeile wie die Ansprechpartner im Vorgang
                // (`vg3-ap-row`, vorgangV3.css): Name als Link zum Kontakt,
                // dahinter die beiden Kommunikations-Icons der Kontakttabelle.
                <div key={p.key} className="vg3-ap-row">
                  {p.contactId ? (
                    <button
                      type="button"
                      className="vg3-ap-name"
                      title="Kontakt öffnen"
                      onClick={() => navigate(`/contacts/${p.contactId}`)}
                    >
                      {p.name}
                    </button>
                  ) : (
                    <span className="vg3-ap-name smb-ohne-kontakt" title={p.name}>
                      {p.name}
                    </span>
                  )}
                  <div className="vg3-ap-funcs" />
                  {p.contactId ? (
                    // Das Popup lädt Nummern und Adressen des Kontakts selbst nach.
                    <KontaktmoeglichkeitCell
                      row={
                        {
                          kontaktId: p.contactId,
                          typ: p.typ ?? "Person",
                          displayName: p.name,
                        } as unknown as ContactDetailsFormValues
                      }
                    />
                  ) : (
                    <span className="kt-comm-actions">
                      <button
                        type="button"
                        className="gic kt-comm"
                        title={p.tel ? `Anrufen: ${p.tel}` : "Keine Telefonnummer hinterlegt"}
                        aria-label={`${p.name} anrufen`}
                        onClick={() => (p.tel ? anrufen(p.tel, p.name) : fehlt("Telefonnummer", p.name))}
                      >
                        {IcoTel}
                      </button>
                      <button
                        type="button"
                        className="gic kt-comm"
                        title={p.email ? `E-Mail an ${p.email}` : "Keine E-Mail-Adresse hinterlegt"}
                        aria-label={`E-Mail an ${p.name}`}
                        onClick={() => (p.email ? mailen(p.email, p.contactId) : fehlt("E-Mail-Adresse", p.name))}
                      >
                        {IcoMail}
                      </button>
                    </span>
                  )}
                  {p.frei !== null && (
                    <button
                      type="button"
                      className="gic smb-weg"
                      title="Entfernen"
                      aria-label={`${p.name} entfernen`}
                      onClick={() => onMappe({ beteiligte: mappe.beteiligte.filter((_, i) => i !== p.frei) })}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {offen && (
        <div
          className="smb-popup-flaeche"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) schliessen();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") schliessen();
          }}
        >
          <div className="smb-popup" role="dialog" aria-modal="true" aria-label="Beteiligten hinzufügen">
            <div className="smb-kt">
              <h2>Beteiligten hinzufügen</h2>
            </div>
            <label className="smb-fld">
              <span className="smb-lbl">Rolle am Schaden</span>
              <input
                list="smb-rollen"
                value={neu.rolle}
                autoFocus
                onChange={(e) => setNeu((n) => ({ ...n, rolle: e.target.value }))}
              />
              <datalist id="smb-rollen">
                {ROLLEN.map((r) => (
                  <option key={r} value={r} />
                ))}
              </datalist>
            </label>
            <div className="smb-fld">
              <span className="smb-lbl">Kontakt aus KMVS</span>
              <KontaktSuche
                kontakt={neu.kontakt}
                vorheriger={null}
                onWahl={(k) => setNeu((n) => ({ ...n, kontakt: k }))}
                suchLabel="Kontakt für den Beteiligten suchen"
              />
            </div>
            {!neu.kontakt && (
              <>
                <p className="smb-oder">oder ohne Kontakt anlegen</p>
                <div className="smb-popup-raster">
                  <label className="smb-fld smb-breit">
                    <span className="smb-lbl">Name</span>
                    <input value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))} />
                  </label>
                  <label className="smb-fld">
                    <span className="smb-lbl">Telefon</span>
                    <input type="tel" value={neu.tel} onChange={(e) => setNeu((n) => ({ ...n, tel: e.target.value }))} />
                  </label>
                  <label className="smb-fld">
                    <span className="smb-lbl">E-Mail</span>
                    <input type="email" value={neu.email} onChange={(e) => setNeu((n) => ({ ...n, email: e.target.value }))} />
                  </label>
                </div>
              </>
            )}
            <div className="smb-popup-fuss">
              <button type="button" className="smb-btn" onClick={schliessen}>
                Abbrechen
              </button>
              <button type="button" className="smb-btn ist-pri" disabled={!name} onClick={hinzufuegen}>
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
